/**
 * Lembur (Overtime) — Modul Kehadiran Tahap 5 (2026-10-12).
 *
 * Lembur = sesi tersendiri (pulang lebih lambat TIDAK otomatis lembur).
 * Alur: Pengajuan/Penugasan → persetujuan/otorisasi → Mulai Lembur →
 * (Catat Istirahat / Lanjutkan) → Selesai Lembur (+ opsi Pulang) →
 * verifikasi realisasi oleh pemeriksa.
 * Bukti: foto langsung (kamera), jam server, geo — komponen Tahap 2.
 * Durasi dipisah: rencana / rentang aktual / istirahat / bersih / disetujui.
 * Tanpa perhitungan upah otomatis pada tahap ini.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Clock, CheckCircle2, XCircle, AlertTriangle, Loader2, MapPin, Camera,
  FilePlus2, Ban, Timer, Coffee, Play, Flag, ShieldAlert, MoonStar, Users,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SearchableSelect from "@/components/SearchableSelect";

const APPROVAL_META = {
  pending_approval: { label: "Menunggu Persetujuan", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  approved: { label: "Disetujui / Terotorisasi", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { label: "Ditolak", cls: "text-rose-700 bg-rose-50 border-rose-200" },
};
const SESSION_META = {
  not_started: "Belum Mulai",
  open: "Lembur Berjalan",
  on_break: "Istirahat",
  closed: "Selesai",
  cancelled: "Dibatalkan",
};
const REALIZATION_META = {
  none: null,
  pending_verification: { label: "Menunggu Verifikasi", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  verified: { label: "Terverifikasi", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { label: "Realisasi Ditolak", cls: "text-rose-700 bg-rose-50 border-rose-200" },
};

function fmtT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtD(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
}
function fmtDur(min) {
  if (min == null) return "—";
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h ? `${h}j ${m}m` : `${m}m`;
}
function todayStr() { return new Date().toISOString().slice(0, 10); }

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Browser tidak mendukung geolokasi."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
      (e) => reject(new Error(`Gagal mengambil lokasi: ${e.message}`)),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

/** Kamera live + capture (pola bukti Tahap 2). onCapture(blob) lalu parent submit. */
function CameraCapture({ testid, onCapture, capturedUrl, onRetake }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [err, setErr] = useState(null);
  const start = async () => {
    try {
      setErr(null);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setActive(true);
    } catch (e) { setErr(`Kamera gagal: ${e.message || e.name}`); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setActive(false); };
  useEffect(() => () => stop(), []);
  const snap = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth || 480; c.height = v.videoHeight || 360;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => { if (b) { onCapture(b); stop(); } }, "image/jpeg", 0.85);
  };
  return (
    <div className="space-y-1" data-testid={testid}>
      {!active && !capturedUrl && (
        <Button type="button" size="sm" variant="outline" onClick={start} className="rounded-none h-7 text-[10px]" data-testid={`${testid}-start`}>
          <Camera size={10} /> Aktifkan Kamera
        </Button>
      )}
      {err && <div className="text-[10px] text-rose-700">{err}</div>}
      {active && (
        <div className="space-y-1">
          <video ref={videoRef} className="w-40 h-28 object-cover bg-slate-900 border border-slate-300" playsInline muted data-testid={`${testid}-video`} />
          <Button type="button" size="sm" onClick={snap} className="rounded-none h-7 text-[10px] bg-[#FF5A1F] hover:bg-[#e04a13]" data-testid={`${testid}-snap`}>Ambil Foto</Button>
        </div>
      )}
      {capturedUrl && (
        <div className="flex items-center gap-2">
          <img src={capturedUrl} alt="Bukti" className="w-20 h-14 object-cover border border-emerald-300" data-testid={`${testid}-preview`} />
          <button type="button" onClick={onRetake} className="text-[10px] text-slate-500 underline" data-testid={`${testid}-retake`}>Ulangi</button>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

function OtForm({ locations, onSaved, onCancel }) {
  const [f, setF] = useState({
    work_date: todayStr(), planned_start: "", planned_end_date: todayStr(), planned_end: "",
    work_description: "", location_id: "", project_ref: "", immediate: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ work_date: f.work_date, planned_start: f.planned_start, planned_end_date: f.planned_end_date, planned_end: f.planned_end, work_description: f.work_description })
        .forEach(([k, v]) => fd.append(k, v));
      if (f.location_id) fd.append("location_id", f.location_id);
      if (f.project_ref) fd.append("project_ref", f.project_ref);
      if (f.immediate) {
        fd.append("immediate", "true");
        const g = await getGeo();
        fd.append("latitude", String(g.lat)); fd.append("longitude", String(g.lon)); fd.append("accuracy", String(g.acc));
        toast.info("Lembur mendadak: lampirkan foto langsung dari aksi Mulai setelah pengajuan dikirim — sesi dicatat menunggu persetujuan.");
        // Foto utk mendadak diwajibkan backend; kirim via start ulang tidak
        // diperlukan — endpoint menerima foto pada create immediate. Karena
        // kamera live butuh interaksi, arahkan user menekan foto setelah form
        // terkirim? Tidak — backend mewajibkan foto pada immediate create.
        // Solusi: blokir immediate tanpa foto di sini.
        toast.error("Untuk lembur mendadak, ajukan biasa lalu gunakan aksi Mulai Lembur (foto langsung) setelah disetujui — atau minta atasan menugaskan.");
        setBusy(false);
        return;
      }
      await api.post("/attendance/overtime", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pengajuan lembur terkirim ke pemeriksa.");
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengirim pengajuan."); }
    finally { setBusy(false); }
  };
  const crossesMidnight = f.planned_end_date > f.work_date;
  return (
    <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="ot-form">
      <div className="font-display text-sm text-[#0B1D3A]">Ajukan Lembur</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <Input type="date" value={f.work_date} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="ot-date" title="Tanggal mulai" />
        <SearchableSelect value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })}
          options={[{ value: "", label: "— Lokasi penugasan (default) —" }, ...locations.map((l) => ({ value: l.id, label: `${l.name} (${l.kind})` }))]}
          placeholder="Lokasi kantor/proyek…" searchPlaceholder="Cari lokasi…" emptyText="Lokasi tidak ditemukan" testid="ot-location" />
        <Input placeholder="Proyek/tugas terkait (opsional)" value={f.project_ref} onChange={(e) => setF({ ...f, project_ref: e.target.value })} data-testid="ot-project" />
        <div className="flex items-center gap-1">
          <Input type="time" value={f.planned_start} onChange={(e) => setF({ ...f, planned_start: e.target.value })} data-testid="ot-start" title="Rencana mulai" />
          <span className="text-slate-400 text-[10px] shrink-0">s/d</span>
          <Input type="time" value={f.planned_end} onChange={(e) => setF({ ...f, planned_end: e.target.value })} data-testid="ot-end" title="Rencana selesai" />
        </div>
        <Input type="date" value={f.planned_end_date} min={f.work_date} onChange={(e) => setF({ ...f, planned_end_date: e.target.value })} data-testid="ot-end-date" title="Tanggal selesai (boleh hari berikutnya)" />
        {crossesMidnight && (
          <div className="flex items-center gap-1 text-[10px] text-sky-700" data-testid="ot-midnight-flag"><MoonStar size={10} /> Lintas tengah malam</div>
        )}
        <Textarea placeholder="Uraian pekerjaan *" value={f.work_description} onChange={(e) => setF({ ...f, work_description: e.target.value })}
          rows={2} className="md:col-span-3 rounded-none" data-testid="ot-desc" />
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Waktu mulai/selesai aktual dicatat dari jam server saat Anda menekan Mulai/Selesai Lembur — bukan waktu rencana maupun persetujuan.
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !f.planned_start || !f.planned_end || !f.work_description.trim()}
          className="rounded-none bg-[#0B1D3A]" data-testid="ot-submit">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />} Kirim Pengajuan
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-none" data-testid="ot-cancel-form">Batal</Button>
      </div>
    </div>
  );
}

function AssignForm({ locations, users, onSaved, onCancel }) {
  const [f, setF] = useState({
    user_id: "", work_date: todayStr(), planned_start: "", planned_end_date: todayStr(),
    planned_end: "", work_description: "", location_id: "", project_ref: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/overtime/assign", {
        user_id: f.user_id, work_date: f.work_date, planned_start: f.planned_start,
        planned_end_date: f.planned_end_date, planned_end: f.planned_end,
        work_description: f.work_description, location_id: f.location_id || null,
        project_ref: f.project_ref || null,
      });
      toast.success("Penugasan lembur terkirim — terotorisasi tanpa persetujuan berulang.");
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal menugaskan."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-sky-300 bg-sky-50/50 p-4 space-y-2" data-testid="ot-assign-form">
      <div className="font-display text-sm text-[#0B1D3A] flex items-center gap-2"><Users size={14} /> Tugaskan Lembur (Otorisasi Atasan)</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <SearchableSelect value={f.user_id} onChange={(v) => setF({ ...f, user_id: v })}
          options={users.map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))}
          placeholder="Pilih karyawan…" searchPlaceholder="Cari nama…" emptyText="Tidak ditemukan" testid="ot-assign-user" />
        <Input type="date" value={f.work_date} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="ot-assign-date" />
        <SearchableSelect value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })}
          options={[{ value: "", label: "— Lokasi penugasan karyawan —" }, ...locations.map((l) => ({ value: l.id, label: `${l.name} (${l.kind})` }))]}
          placeholder="Lokasi…" searchPlaceholder="Cari…" emptyText="—" testid="ot-assign-location" />
        <div className="flex items-center gap-1">
          <Input type="time" value={f.planned_start} onChange={(e) => setF({ ...f, planned_start: e.target.value })} data-testid="ot-assign-start" />
          <span className="text-slate-400 text-[10px] shrink-0">s/d</span>
          <Input type="time" value={f.planned_end} onChange={(e) => setF({ ...f, planned_end: e.target.value })} data-testid="ot-assign-end" />
        </div>
        <Input type="date" value={f.planned_end_date} min={f.work_date} onChange={(e) => setF({ ...f, planned_end_date: e.target.value })} data-testid="ot-assign-end-date" />
        <Input placeholder="Proyek/tugas (opsional)" value={f.project_ref} onChange={(e) => setF({ ...f, project_ref: e.target.value })} data-testid="ot-assign-project" />
        <Textarea placeholder="Uraian pekerjaan *" value={f.work_description} onChange={(e) => setF({ ...f, work_description: e.target.value })}
          rows={2} className="md:col-span-3 rounded-none" data-testid="ot-assign-desc" />
      </div>
      <div className="text-[10px] text-sky-800 italic">Penugasan ini menjadi otorisasi — karyawan tidak perlu persetujuan lagi, namun tetap wajib bukti Mulai/Selesai (foto + lokasi).</div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !f.user_id || !f.planned_start || !f.planned_end || !f.work_description.trim()}
          className="rounded-none bg-[#0B1D3A]" data-testid="ot-assign-submit">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Tugaskan (Otorisasi)
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-none">Batal</Button>
      </div>
    </div>
  );
}

/** Aksi dgn bukti foto+geo (Mulai/Selesai/Selesai&Pulang). */
function EvidenceAction({ label, testid, onSubmit, needReport, extraNote }) {
  const [open, setOpen] = useState(false);
  const [blob, setBlob] = useState(null);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const g = await getGeo();
      const fd = new FormData();
      fd.append("latitude", String(g.lat)); fd.append("longitude", String(g.lon)); fd.append("accuracy", String(g.acc));
      if (blob) fd.append("photo", blob, `lembur_${Date.now()}.jpg`);
      if (needReport) fd.append("report", report);
      if (file) fd.append("attachment", file);
      await onSubmit(fd);
      setOpen(false); setBlob(null); setPreview(null); setReport(""); setFile(null);
    } catch (e) { toast.error(e.message || formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!open) {
    return <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(true)} data-testid={testid}>{label}</Button>;
  }
  return (
    <div className="border border-slate-200 bg-slate-50 p-2 space-y-2 w-full" data-testid={`${testid}-box`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{label}</div>
      <CameraCapture testid={`${testid}-cam`} capturedUrl={preview}
        onCapture={(b) => { setBlob(b); setPreview(URL.createObjectURL(b)); }}
        onRetake={() => { setBlob(null); setPreview(null); }} />
      {needReport && (
        <Textarea placeholder="Laporan hasil *" value={report} onChange={(e) => setReport(e.target.value)} rows={2}
          className="rounded-none text-xs" data-testid={`${testid}-report`} />
      )}
      {needReport && (
        <label className="inline-flex items-center gap-1 text-[10px] text-slate-600 border border-dashed border-slate-300 px-2 py-1 cursor-pointer" data-testid={`${testid}-attach`}>
          Lampiran opsional {file ? `: ${file.name}` : ""}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      )}
      {extraNote && <div className="text-[9px] text-slate-500 italic">{extraNote}</div>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={run} disabled={busy || !blob || (needReport && !report.trim())}
          className="rounded-none h-7 text-[10px] bg-[#0B1D3A]" data-testid={`${testid}-go`}>
          {busy ? <Loader2 size={10} className="animate-spin" /> : <MapPin size={10} />} Konfirmasi
        </Button>
        <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-slate-500 underline">Tutup</button>
      </div>
    </div>
  );
}

function OtCard({ ot, meId, isReview, onChanged }) {
  const [decReason, setDecReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [extDate, setExtDate] = useState(ot.planned_end_date);
  const [extTime, setExtTime] = useState("");
  const [extReason, setExtReason] = useState("");
  const [verifyMin, setVerifyMin] = useState("");
  const [verifyReason, setVerifyReason] = useState("");
  const [, setTick] = useState(0);
  const mine = ot.user_id === meId;

  // Durasi berjalan (tick 30 dtk — status mengikuti kejadian terakhir, bukan real-time).
  useEffect(() => {
    if (ot.session_status !== "open" && ot.session_status !== "on_break") return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(iv);
  }, [ot.session_status]);

  const liveMinutes = useMemo(() => {
    if (!ot.actual_start_at || (ot.session_status !== "open" && ot.session_status !== "on_break")) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(ot.actual_start_at).getTime()) / 60000));
  }, [ot.actual_start_at, ot.session_status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (url, body, okMsg, isForm = false) => {
    setBusy(true);
    try {
      await api.post(url, body, isForm ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);
      toast.success(okMsg);
      onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Aksi gagal."); }
    finally { setBusy(false); }
  };
  const decide = (action) => {
    if (action === "reject" && !decReason.trim()) { toast.error("Alasan penolakan wajib diisi."); return; }
    post(`/attendance/overtime/${ot.id}/decide`, { action, reason: decReason.trim() || null },
      action === "approve" ? "Lembur disetujui." : "Lembur ditolak.");
  };
  const extend = () => {
    const fd = new FormData();
    fd.append("new_planned_end_date", extDate); fd.append("new_planned_end", extTime); fd.append("reason", extReason);
    post(`/attendance/overtime/${ot.id}/extend`, fd, "Perpanjangan diajukan.", true);
    setExtOpen(false);
  };
  const decideExt = (extId, action) => {
    const fd = new FormData();
    fd.append("extension_id", extId); fd.append("action", action);
    if (decReason.trim()) fd.append("reason", decReason.trim());
    post(`/attendance/overtime/${ot.id}/extend-decide`, fd, "Keputusan perpanjangan tersimpan.", true);
  };
  const verify = (action) => {
    const minVal = verifyMin ? parseInt(verifyMin, 10) : null;
    if (action === "approve" && minVal != null && minVal !== (ot.counted_minutes || 0) && !verifyReason.trim()) {
      toast.error("Durasi disetujui berbeda dari realisasi — alasan wajib diisi (audit).");
      return;
    }
    if (action === "reject" && !verifyReason.trim()) { toast.error("Alasan penolakan wajib diisi."); return; }
    post(`/attendance/overtime/${ot.id}/verify`, { action, approved_minutes: minVal, reason: verifyReason.trim() || null },
      action === "approve" ? "Realisasi terverifikasi." : "Realisasi ditolak.");
  };
  const cancel = () => {
    if (!window.confirm("Batalkan sesi lembur ini?")) return;
    post(`/attendance/overtime/${ot.id}/cancel`, null, "Sesi dibatalkan.");
  };

  const am = APPROVAL_META[ot.approval_status] || {};
  const rm = REALIZATION_META[ot.realization_status];
  return (
    <div className="border border-slate-200 bg-white p-3 space-y-2" data-testid={`ot-card-${ot.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-[#0B1D3A]">
            Lembur {fmtD(ot.work_date)} <span className="font-mono">{ot.planned_start}–{ot.planned_end}</span>
            {ot.spans_midnight && <span className="ml-1 text-slate-400 font-normal">(s/d {fmtD(ot.planned_end_date)})</span>}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {ot.location_name || "Lokasi penugasan"}{ot.project_ref ? ` · ${ot.project_ref}` : ""} · Pemeriksa: {ot.reviewer_name || "—"}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {ot.is_holiday && <span className="text-[9px] uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 px-1 py-0.5">Hari Libur</span>}
            {ot.spans_midnight && <span className="text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 px-1 py-0.5"><MoonStar size={8} className="inline" /> Lintas Tengah Malam</span>}
            {ot.sudden && <span className="text-[9px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.5">Mendadak</span>}
            {ot.authorization === "supervisor_assignment" && <span className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-0.5">Otorisasi Atasan</span>}
            {ot.linked_break_id && <span className="text-[9px] uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 px-1 py-0.5">Terkait Tugas Luar (tidak dihitung ganda)</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 ${am.cls}`} data-testid={`ot-approval-${ot.id}`}>{am.label}</span>
          <span className="text-[9px] uppercase tracking-wider text-slate-500" data-testid={`ot-session-${ot.id}`}>{SESSION_META[ot.session_status]}</span>
          {rm && <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 ${rm.cls}`} data-testid={`ot-realization-${ot.id}`}>{rm.label}</span>}
          {ot.needs_review && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"><AlertTriangle size={9} /> Perlu Pemeriksaan</span>}
        </div>
      </div>
      <div className="text-[11px] text-slate-700">{ot.work_description}</div>
      {/* Panel durasi terpisah */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-1 text-center" data-testid={`ot-durations-${ot.id}`}>
        <div className="border border-slate-100 bg-slate-50 px-1 py-1"><div className="text-[8px] uppercase tracking-wider text-slate-500">Rencana</div><div className="text-[11px] font-semibold tabular-nums">{fmtDur(ot.planned_minutes)}</div></div>
        <div className="border border-slate-100 bg-slate-50 px-1 py-1"><div className="text-[8px] uppercase tracking-wider text-slate-500">Rentang Aktual</div><div className="text-[11px] font-semibold tabular-nums">{ot.actual_span_minutes != null ? fmtDur(ot.actual_span_minutes) : (liveMinutes != null ? `${fmtDur(liveMinutes)} …` : "—")}</div></div>
        <div className="border border-slate-100 bg-slate-50 px-1 py-1"><div className="text-[8px] uppercase tracking-wider text-slate-500">Istirahat</div><div className="text-[11px] font-semibold tabular-nums">{fmtDur(ot.break_minutes)}</div></div>
        <div className="border border-slate-100 bg-slate-50 px-1 py-1"><div className="text-[8px] uppercase tracking-wider text-slate-500">Aktual Bersih</div><div className="text-[11px] font-semibold tabular-nums">{fmtDur(ot.net_actual_minutes)}</div></div>
        <div className="border border-slate-100 bg-slate-50 px-1 py-1"><div className="text-[8px] uppercase tracking-wider text-slate-500">Disetujui</div><div className="text-[11px] font-semibold tabular-nums text-emerald-700">{ot.approved_minutes != null ? fmtDur(ot.approved_minutes) : "—"}</div></div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
        {ot.actual_start_at && <span>Mulai aktual: <span className="font-mono">{fmtDT(ot.actual_start_at)}</span></span>}
        {ot.session_status !== "closed" && ot.session_status !== "cancelled" && <span>Estimasi selesai: <span className="font-mono">{ot.planned_end} ({fmtD(ot.planned_end_date)})</span></span>}
        {ot.actual_end_at && <span>Selesai: <span className="font-mono">{fmtDT(ot.actual_end_at)}</span></span>}
        {ot.overlap_normal_minutes > 0 && <span className="text-amber-700">Irisan kerja normal: {fmtDur(ot.overlap_normal_minutes)} (tidak dihitung lembur)</span>}
        {ot.needs_review_reason && <span className="text-amber-700">{ot.needs_review_reason}</span>}
        {ot.finish_report && <span className="text-slate-600">Laporan: {ot.finish_report}</span>}
        {ot.decision_reason && <span>Keputusan: {ot.decision_reason}</span>}
        {ot.verify_reason && <span>Verifikasi: {ot.verify_reason}</span>}
      </div>
      {(ot.plan_history || []).length > 1 && (
        <div className="text-[10px] text-slate-500">Riwayat rencana: {ot.plan_history.map((h, i) => `${h.planned_end_date} ${h.planned_end}${i === 0 ? " (awal)" : ""}`).join(" → ")}</div>
      )}
      {(ot.extensions || []).map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-2 text-[10px] border border-slate-100 bg-slate-50 px-2 py-1" data-testid={`ot-ext-${e.id}`}>
          <span>Perpanjangan → {e.new_planned_end_date} {e.new_planned_end} ({e.reason}) — {e.status === "pending" ? "menunggu" : e.status === "approved" ? "disetujui" : "ditolak"}</span>
          {isReview && e.status === "pending" && (
            <span className="flex gap-1">
              <button type="button" onClick={() => decideExt(e.id, "approve")} className="text-emerald-700 font-semibold" data-testid={`ot-ext-approve-${e.id}`}>Setujui</button>
              <button type="button" onClick={() => decideExt(e.id, "reject")} className="text-rose-600 font-semibold" data-testid={`ot-ext-reject-${e.id}`}>Tolak</button>
            </span>
          )}
        </div>
      ))}

      {/* Aksi pemilik */}
      {mine && (
        <div className="flex flex-wrap items-start gap-2 border-t border-slate-100 pt-2">
          {ot.session_status === "not_started" && ot.approval_status === "approved" && (
            <EvidenceAction label="Mulai Lembur" testid={`ot-start-${ot.id}`}
              onSubmit={(fd) => api.post(`/attendance/overtime/${ot.id}/start`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                .then(() => { toast.success("Lembur dimulai — waktu server tercatat."); onChanged(); })} />
          )}
          {ot.session_status === "open" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" disabled={busy}
              onClick={() => post(`/attendance/overtime/${ot.id}/break-toggle`, null, "Istirahat dicatat.")} data-testid={`ot-break-${ot.id}`}>
              <Coffee size={10} /> Catat Istirahat
            </Button>
          )}
          {ot.session_status === "on_break" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" disabled={busy}
              onClick={() => post(`/attendance/overtime/${ot.id}/break-toggle`, null, "Lembur dilanjutkan.")} data-testid={`ot-resume-${ot.id}`}>
              <Play size={10} /> Lanjutkan Lembur
            </Button>
          )}
          {(ot.session_status === "open" || ot.session_status === "on_break") && (
            <>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setExtOpen(!extOpen)} data-testid={`ot-extend-${ot.id}`}>
                <Timer size={10} /> Ajukan Perpanjangan
              </Button>
              <EvidenceAction label="Selesai Lembur" testid={`ot-finish-${ot.id}`} needReport
                onSubmit={(fd) => api.post(`/attendance/overtime/${ot.id}/finish`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                  .then(() => { toast.success("Lembur selesai — menunggu verifikasi pemeriksa."); onChanged(); })} />
              <EvidenceAction label="Selesai Lembur & Pulang" testid={`ot-finish-out-${ot.id}`} needReport
                extraNote="Untuk lembur yang langsung menyambung hari kerja — absen pulang dicatat dalam satu transaksi."
                onSubmit={(fd) => api.post(`/attendance/overtime/${ot.id}/finish-clock-out`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                  .then(() => { toast.success("Lembur selesai & absen pulang tercatat."); onChanged(); })} />
            </>
          )}
          {ot.session_status === "not_started" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-rose-600" onClick={cancel} data-testid={`ot-cancel-${ot.id}`}>
              <Ban size={10} /> Batalkan
            </Button>
          )}
        </div>
      )}
      {extOpen && (
        <div className="border border-sky-200 bg-sky-50 p-2 flex flex-wrap items-center gap-2" data-testid={`ot-extend-form-${ot.id}`}>
          <Input type="date" value={extDate} min={ot.work_date} onChange={(e) => setExtDate(e.target.value)} className="h-7 w-36 text-[11px]" data-testid={`ot-ext-date-${ot.id}`} />
          <Input type="time" value={extTime} onChange={(e) => setExtTime(e.target.value)} className="h-7 w-24 text-[11px]" data-testid={`ot-ext-time-${ot.id}`} />
          <Input placeholder="Alasan perpanjangan *" value={extReason} onChange={(e) => setExtReason(e.target.value)} className="h-7 text-[11px] flex-1 min-w-[160px]" data-testid={`ot-ext-reason-${ot.id}`} />
          <Button size="sm" onClick={extend} disabled={busy || !extTime || !extReason.trim()} className="rounded-none h-7 text-[10px] bg-[#0B1D3A]" data-testid={`ot-ext-submit-${ot.id}`}>Kirim</Button>
        </div>
      )}
      {/* Aksi pemeriksa: persetujuan pengajuan */}
      {isReview && ot.approval_status === "pending_approval" && (
        <div className="border-t border-slate-100 pt-2 space-y-2" data-testid={`ot-decide-${ot.id}`}>
          <Textarea placeholder="Alasan keputusan (wajib untuk tolak)" value={decReason} onChange={(e) => setDecReason(e.target.value)} rows={1} className="rounded-none text-xs" data-testid={`ot-decide-reason-${ot.id}`} />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => decide("approve")} disabled={busy} className="rounded-none h-7 text-[10px] bg-emerald-700 hover:bg-emerald-800" data-testid={`ot-approve-${ot.id}`}><CheckCircle2 size={11} /> Setujui</Button>
            <Button size="sm" onClick={() => decide("reject")} disabled={busy} className="rounded-none h-7 text-[10px] bg-rose-600 hover:bg-rose-700" data-testid={`ot-reject-${ot.id}`}><XCircle size={11} /> Tolak</Button>
          </div>
        </div>
      )}
      {/* Aksi pemeriksa: verifikasi realisasi */}
      {isReview && ot.realization_status === "pending_verification" && (
        <div className="border border-amber-200 bg-amber-50 p-2 space-y-2" data-testid={`ot-verify-${ot.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1"><Flag size={10} /> Verifikasi Realisasi</div>
          <div className="text-[10px] text-slate-600">Realisasi bersih {fmtDur(ot.net_actual_minutes)} · terhitung lembur {fmtDur(ot.counted_minutes)}{ot.beyond_plan ? " · MELEBIHI RENCANA" : ""}</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" min={0} placeholder={`Durasi disetujui (menit, default ${ot.counted_minutes ?? 0})`} value={verifyMin}
              onChange={(e) => setVerifyMin(e.target.value)} className="h-7 w-52 text-[11px]" data-testid={`ot-verify-min-${ot.id}`} />
            <Input placeholder="Alasan (wajib bila durasi beda / tolak)" value={verifyReason} onChange={(e) => setVerifyReason(e.target.value)}
              className="h-7 text-[11px] flex-1 min-w-[200px]" data-testid={`ot-verify-reason-${ot.id}`} />
            <Button size="sm" onClick={() => verify("approve")} disabled={busy} className="rounded-none h-7 text-[10px] bg-emerald-700 hover:bg-emerald-800" data-testid={`ot-verify-approve-${ot.id}`}><CheckCircle2 size={11} /> Verifikasi</Button>
            <Button size="sm" onClick={() => verify("reject")} disabled={busy} className="rounded-none h-7 text-[10px] bg-rose-600 hover:bg-rose-700" data-testid={`ot-verify-reject-${ot.id}`}><XCircle size={11} /> Tolak</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RekapView() {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/attendance/overtime/rekap?month=${month}`); setData(r.data); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="ot-rekap">
      <div className="flex items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="ot-rekap-month" />
        <span className="text-[10px] text-slate-500 italic">Upah lembur tidak dihitung otomatis pada tahap ini.</span>
      </div>
      {loading && <div className="text-xs text-slate-500"><Loader2 className="inline animate-spin" size={12} /> Memuat…</div>}
      {data && (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="sticky top-0 bg-slate-50 text-left px-3 py-2">Karyawan</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Sesi</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Rencana</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Aktual Bersih</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Istirahat</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Irisan Normal</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Terhitung</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Disetujui</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Pending</th>
                <th className="sticky top-0 bg-slate-50 text-right px-3 py-2">Libur</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 italic">Belum ada lembur disetujui bulan ini.</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ot-rekap-row-${r.user_id}`}>
                  <td className="px-3 py-2"><div className="font-semibold">{r.user_name}</div><div className="text-[10px] text-slate-400">{r.user_division}</div></td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.sessions}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtDur(r.planned_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtDur(r.net_actual_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtDur(r.break_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtDur(r.overlap_normal_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtDur(r.counted_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{fmtDur(r.approved_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.pending_verification > 0 ? <span className="text-amber-700">{r.pending_verification}</span> : 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.holiday_sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LemburPanel() {
  const [view, setView] = useState("mine");
  const [mine, setMine] = useState([]);
  const [review, setReview] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [noReviewer, setNoReviewer] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, rv, locs, meR, metaR, asgR, stR] = await Promise.all([
        api.get("/attendance/overtime?role=mine"),
        api.get("/attendance/overtime?role=review").catch(() => ({ data: [] })),
        api.get("/attendance/locations"),
        api.get("/auth/me"),
        api.get("/attendance/meta"),
        api.get("/attendance/assignments?active_only=true").catch(() => ({ data: [] })),
        api.get("/attendance/settings").catch(() => ({ data: {} })),
      ]);
      setMine(m.data || []); setReview(rv.data || []); setLocations(locs.data || []);
      setMe(meR.data); setMeta(metaR.data);
      const anyReviewer = (asgR.data || []).some((a) => a.reviewer_user_id);
      setNoReviewer(!anyReviewer && !(stR.data || {}).default_reviewer_user_id);
      if (metaR.data?.can_approve) {
        api.get("/users").then((u) => setUsers(u.data || [])).catch(() => {});
      }
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat lembur."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pendingReview = useMemo(
    () => review.filter((o) => (o.approval_status === "pending_approval" || o.realization_status === "pending_verification") && o.user_id !== me?.id),
    [review, me]);
  const canAssign = !!meta?.can_approve;

  if (loading) return <div className="p-6 text-center text-slate-500 text-xs"><Loader2 className="inline animate-spin" size={14} /> Memuat lembur…</div>;

  return (
    <div className="space-y-3" data-testid="att-ot-panel">
      {noReviewer && (
        <div className="border border-rose-200 bg-rose-50 px-3 py-2 flex items-start gap-2" data-testid="ot-no-reviewer">
          <ShieldAlert size={14} className="text-rose-700 mt-0.5 shrink-0" />
          <div className="text-[11px] text-rose-800"><span className="font-semibold uppercase tracking-wider">Pemeriksa belum dikonfigurasi. </span>
            Pengajuan lembur tidak dapat dikirim sampai HR mengisi pemeriksa pada penugasan atau pemeriksa default.</div>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
          <button type="button" onClick={() => setView("mine")} data-testid="ot-view-mine"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${view === "mine" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Lembur Saya ({mine.length})
          </button>
          <button type="button" onClick={() => setView("review")} data-testid="ot-view-review"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${view === "review" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Perlu Persetujuan ({pendingReview.length})
          </button>
          {canAssign && (
            <button type="button" onClick={() => setView("rekap")} data-testid="ot-view-rekap"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${view === "rekap" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
              Rekap
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canAssign && view !== "rekap" && (
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => { setShowAssign(!showAssign); setShowForm(false); }} data-testid="ot-assign-btn">
              <Users size={14} /> Tugaskan Lembur
            </Button>
          )}
          {view === "mine" && (
            <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => { setShowForm(!showForm); setShowAssign(false); }} data-testid="ot-new-btn">
              <FilePlus2 size={14} /> Ajukan Lembur
            </Button>
          )}
        </div>
      </div>
      {showAssign && <AssignForm locations={locations} users={users} onSaved={() => { setShowAssign(false); load(); }} onCancel={() => setShowAssign(false)} />}
      {showForm && view === "mine" && <OtForm locations={locations} onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {view === "rekap" ? <RekapView /> : (
        <div className="space-y-2">
          {(view === "mine" ? mine : review).length === 0 && (
            <div className="border border-slate-200 px-3 py-6 text-center text-slate-400 italic text-xs">
              {view === "mine" ? "Belum ada sesi lembur. Pulang lebih lambat tanpa pengajuan bukan lembur." : "Tidak ada lembur yang perlu Anda periksa."}
            </div>
          )}
          {(view === "mine" ? mine : review).map((o) => (
            <OtCard key={o.id} ot={o} meId={me?.id} isReview={view === "review" && o.user_id !== me?.id} onChanged={load} />
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-500 italic">
        Sesi lembur terbuka tidak ditutup otomatis — gunakan koreksi bila lupa. Durasi di luar rencana tetap disimpan dan ditandai untuk pemeriksaan.
      </div>
    </div>
  );
}
